import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorState,
  SeverityBadge,
  Skeleton,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { Link } from 'react-router-dom';


import {
  fetchSourceCatalog,
  listBuildingSources,
  listViolations,
  refreshBuildingSource,
  toggleBuildingSource,
  type Violation,
} from '../../../api/violations.js';
import {
  agencyLabel,
  formatDate,
  formatPollInterval,
  formatRelativeDays,
  severityLabel,
  statusLabel,
} from '../violations/lib.js';

import type { ViolationSourceId } from '@sitewise/shared';

/**
 * Building-detail "Violations" tab. Two stacked cards:
 *   1. Open violations for this building (bridge to /violations/:id).
 *   2. Monitored sources — toggle + manual refresh per source.
 */
export function BuildingViolationsTab({ buildingId }: { buildingId: string }) {
  const queryClient = useQueryClient();

  const violationsQuery = useQuery({
    queryKey: ['violations', 'list', { buildingId, status: ['open', 'in_review'] }],
    queryFn: () =>
      listViolations({
        buildingId,
        status: ['open', 'in_review'],
        limit: 25,
      }),
  });
  const sourcesQuery = useQuery({
    queryKey: ['violations', 'sources', buildingId],
    queryFn: () => listBuildingSources(buildingId),
  });
  const catalogQuery = useQuery({
    queryKey: ['violations', 'catalog'],
    queryFn: fetchSourceCatalog,
  });

  const toggleMutation = useMutation({
    mutationFn: ({
      sourceId,
      isEnabled,
    }: {
      sourceId: ViolationSourceId;
      isEnabled: boolean;
    }) => toggleBuildingSource(buildingId, sourceId, { isEnabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['violations', 'sources'] }),
  });
  const refreshMutation = useMutation({
    mutationFn: (sourceId: ViolationSourceId) =>
      refreshBuildingSource(buildingId, sourceId),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold">Open violations</h3>
          {violationsQuery.isError ? (
            <ErrorState description="Could not load violations." />
          ) : violationsQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (violationsQuery.data?.violations ?? []).length === 0 ? (
            <EmptyState
              icon={AlertTriangle}
              title="No open violations"
              description="When agencies issue a violation against this building, it will appear here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {violationsQuery.data!.violations.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/violations/${v.id}`}
                      className="block truncate font-medium hover:underline"
                    >
                      {v.summary}
                    </Link>
                    <div className="text-xs text-fg-muted">
                      {agencyLabel(v.agency)} · {v.kind} · issued {formatDate(v.issuedAt)} (
                      {formatRelativeDays(v.issuedAt)})
                    </div>
                  </div>
                  <SeverityBadge level={v.severity} subtle>
                    {severityLabel(v.severity)}
                  </SeverityBadge>
                  <span className="text-xs text-fg-muted">{statusLabel(v.status)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="mb-1 text-sm font-semibold">Monitored sources</h3>
          <p className="mb-3 text-xs text-fg-muted">
            Disable individual sources you don&rsquo;t want this building polled against (e.g.
            non-residential buildings can disable HPD complaints).
          </p>
          {sourcesQuery.isError || catalogQuery.isError ? (
            <ErrorState description="Could not load source list." />
          ) : sourcesQuery.isLoading || catalogQuery.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (sourcesQuery.data?.sources ?? []).length === 0 ? (
            <EmptyState
              icon={AlertTriangle}
              title="No monitored sources"
              description="This building isn't enrolled — likely outside NYC. Save the borough on the building to enroll it."
            />
          ) : (
            <ul className="divide-y divide-border">
              {sourcesQuery.data!.sources.map((s) => {
                const desc = catalogQuery.data?.sources.find((c) => c.id === s.sourceId);
                return (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">
                        {desc?.displayName ?? s.sourceId}
                      </div>
                      <div className="truncate text-xs text-fg-muted">
                        {desc ? formatPollInterval(desc.defaultPollIntervalMs) : ''}
                        {s.lastSuccessAt
                          ? ` · last success ${formatRelativeDays(s.lastSuccessAt)}`
                          : ' · never polled'}
                        {s.lastFailureReason ? ` · last error: ${s.lastFailureReason}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={refreshMutation.isPending}
                      onClick={() => refreshMutation.mutate(s.sourceId)}
                      aria-label={`Refresh ${s.sourceId}`}
                    >
                      <RefreshCcw className="h-3.5 w-3.5" />
                    </Button>
                    <label className="flex cursor-pointer items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={s.isEnabled}
                        onChange={(e) =>
                          toggleMutation.mutate({
                            sourceId: s.sourceId,
                            isEnabled: e.target.checked,
                          })
                        }
                      />
                      {s.isEnabled ? 'On' : 'Off'}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export type { Violation };
